"use client";

const SUBMIT_BUTTON_SELECTOR = "#submit-button";

/**
 * Instant submit busy on the button — paints without waiting for React
 * reconciliation (keeps INP presentation off the flushSync critical path).
 */
export function commitSubmitBusyDom(btn?: HTMLElement | null): void {
  const target = btn || document.querySelector(SUBMIT_BUTTON_SELECTOR);
  if (!target || (target as HTMLElement).dataset.submitDomBusy === "1") return;
  (target as HTMLElement).dataset.submitDomBusy = "1";
  (target as HTMLElement).setAttribute("aria-busy", "true");
  try {
    // Only set disabled if this element is a button or has disabled property
    if ((target as HTMLButtonElement).disabled !== undefined) {
      (target as HTMLButtonElement).disabled = true;
    }
  } catch {}
}

/** Clears DOM busy markers. Do not force-enable — React `disabled` is source of truth (validity + loading). Forcing `disabled=false` here hurt INP recovery and re-enabled invalid Submit. */
export function clearSubmitBusyDom(btn?: HTMLElement | null): void {
  const target = btn || document.querySelector(SUBMIT_BUTTON_SELECTOR);
  if (!target) return;
  try {
    delete (target as HTMLElement).dataset.submitDomBusy;
    (target as HTMLElement).removeAttribute("aria-busy");
  } catch {}
}

/**
 * Yields so the browser can paint DOM busy before validation/API.
 * scheduler.yield is cheaper than a full animation frame on Chromium (~8ms vs ~16ms).
 */
async function yieldBeforeHeavyWork(): Promise<void> {
  const sched = (
    globalThis as typeof globalThis & {
      scheduler?: { yield?: () => Promise<void> };
    }
  ).scheduler;
  if (typeof sched?.yield === "function") {
    await sched.yield();
    return;
  }
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

/**
 * DOM-first busy → non-blocking React busy → yield → validate/API.
 * Replaces flushSync + rAF which blocked the main thread for ~32–50ms on 4× CPU.
 */
export async function beginSubmitInteraction(
  commitBusy: () => void,
  /**
   * Optional event or element to scope the DOM busy marker to the initiating
   * submit button. If omitted, falls back to the global selector.
   */
  eventOrElement?: Event | HTMLElement | null,
): Promise<() => void> {
  // Resolve a button element from the event if possible
  let btn: HTMLElement | null = null;
  try {
    if (eventOrElement) {
      const maybeNative = eventOrElement as any;
      const currentTargetEl =
        maybeNative?.currentTarget instanceof HTMLElement
          ? maybeNative.currentTarget
          : maybeNative?.nativeEvent?.currentTarget instanceof HTMLElement
            ? maybeNative.nativeEvent.currentTarget
            : null;

      if (currentTargetEl instanceof HTMLElement) {
        btn = currentTargetEl;
      } else if (eventOrElement instanceof HTMLElement) {
        btn = eventOrElement;
      } else if ((eventOrElement as Event).type) {
        const ev = eventOrElement as Event;
        // Prefer the submitter (for form submit events)
        // @ts-ignore - submitter is not yet widely typed on Event
        if ((ev as any).submitter instanceof HTMLElement) {
          btn = (ev as any).submitter as HTMLElement;
        } else if (typeof (ev as any).composedPath === "function") {
          const path = (ev as any).composedPath();
          btn =
            path.find(
              (p: any) =>
                p instanceof HTMLElement &&
                (p.tagName === "BUTTON" ||
                  (p.getAttribute && p.getAttribute("type") === "submit")),
            ) || null;
        } else if (ev.target instanceof HTMLElement) {
          const t = ev.target as HTMLElement;
          const fromTarget = t.closest?.("button, [type='submit']");
          if (fromTarget instanceof HTMLElement) {
            btn = fromTarget;
          } else if (t.tagName === "BUTTON" || t.getAttribute("type") === "submit") {
            btn = t;
          }
        }
      }
    }
  } catch {}

  // Paint-first DOM busy state is applied immediately so the initiating button
  // shows a busy state before React commits. Keep the actual React state update
  // after the paint yield so the click path is not blocked by rerender work in
  // the same interaction turn.
  commitSubmitBusyDom(btn);
  await yieldBeforeHeavyWork();
  commitBusy();

  // Return a cleanup function that clears the DOM busy state for the button we set
  return () => {
    clearSubmitBusyDom(btn);
  };
}

/**
 * Yield one frame when busy UI is already committed (e.g. OTP verify after local busy).
 */
export async function yieldToNextPaint(): Promise<void> {
  await yieldBeforeHeavyWork();
}
